// Scene config — fully data-driven, JSON-serializable.
// All geometry lives in a single entities[] array with tagged types.
// Editor saves this directly. Maps are JSON files.

import { FP, type Fixed } from "./fixed.js";
import { type World, type System, type Vec3Store, type IntStore, type TagStore, BitSet, query } from "./ecs.js";
import { aabbToBrush, aabb, brush, type Brush, type Plane } from "./collision.js";
import { createPlayerController } from "./movement.js";
import { createPhysicsSystem } from "./physics.js";
import { createBuoyancySystem } from "./buoyancy.js";
import type { PointLightDef } from "./lighting.js";
import { simEvents, SimEventType } from "./events.js";

// --- Utility ---

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16 & 0xff) / 255, (n >> 8 & 0xff) / 255, (n & 0xff) / 255];
}

// --- Entity types (tagged union) ---

export type BrushEntity = {
  type: "brush";
  x: number; y: number; z: number;
  w: number; h: number; d: number;
  color: string;
  emissive?: number;
  roughness?: number;
  noVoxel?: boolean;
  material?: string;  // physical surface material ("interior", "exterior")
};

export type RampEntity = {
  type: "ramp";
  x: number; y?: number; z: number;
  length: number; height: number;
  baseW: number; topW: number;
  leftColor: [number, number, number];
  rightColor: [number, number, number];
  topColor: [number, number, number];
  curveX?: number;  // horizontal curve offset (bezier midpoint displacement)
  curveY?: number;  // vertical curve offset
  segments?: number; // curve segment count (default 12)
  yaw?: number;     // rotation in radians around Y axis (0 = faces along Z)
  topOffset?: number; // lateral shift of top edge (0 = centered, positive = shift right)
  frontY?: number;    // Y offset at front end (0 = level, positive = front raised)
  material?: string;  // physical surface material ("interior", "exterior")
};

export type SpawnEntity = {
  type: "spawn";
  pos: [number, number, number];
  size?: number;
};

export type LightEntity = {
  type: "light";
  pos: [number, number, number];
  radius: number;
  color: [number, number, number];
  intensity: number;
};

export type TriggerAction = "start" | "end" | "checkpoint" | "teleport" | "push";

export type TriggerEntity = {
  type: "trigger";
  x: number; y: number; z: number;
  w: number; h: number; d: number;
  action: TriggerAction;
  target?: [number, number, number]; // teleport destination
  param?: number;                     // checkpoint index, push force
};

export type Entity = BrushEntity | RampEntity | SpawnEntity | LightEntity | TriggerEntity;

// --- SceneConfig ---

export interface SceneConfig {
  entities: Entity[];
  mode?: string;   // game mode identifier (interpreted by game, e.g., "surf", "freeplay")
  ocean?: boolean;
  gravity?: number;
  voxelBounds?: { min: [number, number, number]; max: [number, number, number] };
  fog?: { color: [number, number, number]; density: number };
  giStrength?: number;
  reflStrength?: number;
  sunDir?: [number, number, number];
}

// --- Legacy compat types (used by renderer internals) ---

export type BoxDef = { x: number; y: number; z: number; w: number; h: number; d: number };

export type ComponentStores = {
  position: Vec3Store;
  velocity: Vec3Store;
  playerId: IntStore;
  renderSize: IntStore;
  renderColor: TagStore;
};

export interface SceneRenderConfig {
  ocean: boolean;
  voxelBounds: { min: [number, number, number]; max: [number, number, number] };
  lights: PointLightDef[];
  fog: { color: [number, number, number]; density: number };
  giStrength: number;
  reflStrength: number;
  sunDir: [number, number, number];
  voxelBoxes: Array<BoxDef & { r: number; g: number; b: number; emissive?: number }>;
  sceneBoxes: Array<BoxDef & { color: string; emissive?: number; roughness?: number }>;
  meshVertices?: Float32Array;
}

/** Extract entities of a given type */
export function entitiesOfType<T extends Entity["type"]>(
  entities: Entity[], type: T
): Extract<Entity, { type: T }>[] {
  return entities.filter(e => e.type === type) as Extract<Entity, { type: T }>[];
}

// --- Ramp helpers ---

/** Rotate a point (x,z) around a center by angle (radians) in the XZ plane */
function rotXZ(px: number, pz: number, cx: number, cz: number, cos: number, sin: number): [number, number] {
  const dx = px - cx, dz = pz - cz;
  return [cx + dx * cos - dz * sin, cz + dx * sin + dz * cos];
}

/** Rotate a 3D point around (cx, cz) by yaw, keeping Y unchanged */
function rotPoint(p: [number, number, number], cx: number, cz: number, cos: number, sin: number): [number, number, number] {
  const [rx, rz] = rotXZ(p[0], p[2], cx, cz, cos, sin);
  return [rx, p[1], rz];
}

type Cross = { bl: [number,number,number]; br: [number,number,number]; tl: [number,number,number]; tr: [number,number,number] };

/** Sample cross-sections along a ramp's Bezier path with tangent-tilted profiles.
 *  Cross-sections rotate to be perpendicular to the curve tangent. */
export function sampleRampSections(r: RampEntity): Cross[] {
  const frontY = r.frontY ?? 0;
  const hasCurve = (r.curveX && Math.abs(r.curveX) >= 0.5) || (r.curveY && Math.abs(r.curveY) >= 0.5);
  const hasVertical = hasCurve || Math.abs(frontY) >= 0.1;
  const N = hasVertical ? (r.segments ?? 12) : 1;
  const halfLen = r.length / 2;
  const halfBase = r.baseW / 2, halfTop = r.topW / 2;
  const by = r.y ?? 0;
  const curveX = r.curveX ?? 0, curveY = r.curveY ?? 0;
  const yaw = r.yaw ?? 0;
  const cosY = Math.cos(yaw), sinY = Math.sin(yaw);

  // Bezier control points — frontY tilts the front end
  const ax = r.x, ay = by + frontY, az = r.z - halfLen;
  const bx = r.x + curveX, bby = by + curveY + frontY / 2, bz = r.z;
  const cx = r.x, cy = by, cz = r.z + halfLen;

  const sections: Cross[] = [];

  for (let i = 0; i <= N; i++) {
    const t = i / N;
    // Position on Bezier
    const px = (1-t)*(1-t)*ax + 2*(1-t)*t*bx + t*t*cx;
    const py = (1-t)*(1-t)*ay + 2*(1-t)*t*bby + t*t*cy;
    const pz = (1-t)*(1-t)*az + 2*(1-t)*t*bz + t*t*cz;

    // Tangent (derivative of quadratic Bezier)
    const tx = 2*(1-t)*(bx-ax) + 2*t*(cx-bx);
    const ty = 2*(1-t)*(bby-ay) + 2*t*(cy-bby);
    const tz = 2*(1-t)*(bz-az) + 2*t*(cz-bz);

    // Build a local frame: forward = tangent, up derived from cross products
    // Forward direction (tangent normalized)
    const flen = Math.sqrt(tx*tx + ty*ty + tz*tz);
    const fx = tx/flen, fy = ty/flen, fz = tz/flen;

    // Right = forward × worldUp (0,1,0), then normalize
    let rx = fz, ry = 0, rz = -fx;
    const rlen = Math.sqrt(rx*rx + ry*ry + rz*rz);
    if (rlen > 1e-6) { rx /= rlen; ry /= rlen; rz /= rlen; }
    else { rx = 1; ry = 0; rz = 0; } // degenerate (vertical tangent)

    // Up = forward × right
    const ux = fy*rz - fz*ry, uy = fz*rx - fx*rz, uz = fx*ry - fy*rx;

    // Place cross-section corners using right and up vectors
    // topOffset shifts the top edge laterally along the right vector
    const h = r.height;
    const tOff = r.topOffset ?? 0;
    let bl: [number,number,number] = [px - halfBase*rx, py - halfBase*ry, pz - halfBase*rz];
    let br: [number,number,number] = [px + halfBase*rx, py + halfBase*ry, pz + halfBase*rz];
    let tl: [number,number,number] = [px + tOff*rx - halfTop*rx + h*ux, py + tOff*ry - halfTop*ry + h*uy, pz + tOff*rz - halfTop*rz + h*uz];
    let tr: [number,number,number] = [px + tOff*rx + halfTop*rx + h*ux, py + tOff*ry + halfTop*ry + h*uy, pz + tOff*rz + halfTop*rz + h*uz];

    // Apply yaw rotation
    if (yaw !== 0) {
      bl = rotPoint(bl, r.x, r.z, cosY, sinY);
      br = rotPoint(br, r.x, r.z, cosY, sinY);
      tl = rotPoint(tl, r.x, r.z, cosY, sinY);
      tr = rotPoint(tr, r.x, r.z, cosY, sinY);
    }

    sections.push({ bl, br, tl, tr });
  }
  return sections;
}

// --- Ramp mesh generation ---

/** Generate mesh for a ramp. Supports curves, tangent-tilted sections, and yaw rotation.
 *  Side faces use smooth (per-vertex) normals for curved ramps. */
export function surfRampMesh(r: RampEntity): number[] {
  const verts: number[] = [];
  const sections = sampleRampSections(r);
  const N = sections.length - 1;

  const [rL, gL, bL] = r.leftColor;
  const [rR, gR, bR] = r.rightColor;
  const [rT, gT, bT] = r.topColor;

  function vert(p: [number,number,number], nx: number, ny: number, nz: number, cr: number, cg: number, cb: number) {
    verts.push(p[0],p[1],p[2], nx,ny,nz, cr,cg,cb);
  }

  function flatQuad(
    a: [number,number,number], b: [number,number,number],
    c: [number,number,number], d: [number,number,number],
    cr: number, cg: number, cb: number,
  ) {
    const e1x = b[0]-a[0], e1y = b[1]-a[1], e1z = b[2]-a[2];
    const e2x = d[0]-a[0], e2y = d[1]-a[1], e2z = d[2]-a[2];
    let nx = e1y*e2z - e1z*e2y, ny = e1z*e2x - e1x*e2z, nz = e1x*e2y - e1y*e2x;
    const nl = Math.sqrt(nx*nx + ny*ny + nz*nz);
    if (nl > 0) { nx /= nl; ny /= nl; nz /= nl; }
    vert(a,nx,ny,nz,cr,cg,cb); vert(b,nx,ny,nz,cr,cg,cb); vert(c,nx,ny,nz,cr,cg,cb);
    vert(a,nx,ny,nz,cr,cg,cb); vert(c,nx,ny,nz,cr,cg,cb); vert(d,nx,ny,nz,cr,cg,cb);
  }

  // Compute per-section surface normals for smooth shading
  type SectionNormals = { left: [number,number,number]; right: [number,number,number]; top: [number,number,number]; bot: [number,number,number] };
  const secNormals: SectionNormals[] = [];
  for (let i = 0; i <= N; i++) {
    const s = sections[i];
    // Left face normal: perpendicular to (bl→tl) edge, pointing outward (left)
    // Use adjacent sections to get tangent direction for cross product
    const prev = sections[Math.max(0, i - 1)];
    const next = sections[Math.min(N, i + 1)];
    const tx = next.bl[0]-prev.bl[0], ty = next.bl[1]-prev.bl[1], tz = next.bl[2]-prev.bl[2];

    const edgeLx = s.tl[0]-s.bl[0], edgeLy = s.tl[1]-s.bl[1], edgeLz = s.tl[2]-s.bl[2];
    let lnx = edgeLy*tz - edgeLz*ty, lny = edgeLz*tx - edgeLx*tz, lnz = edgeLx*ty - edgeLy*tx;
    let ll = Math.sqrt(lnx*lnx+lny*lny+lnz*lnz);
    if (ll > 0) { lnx/=ll; lny/=ll; lnz/=ll; }

    const edgeRx = s.tr[0]-s.br[0], edgeRy = s.tr[1]-s.br[1], edgeRz = s.tr[2]-s.br[2];
    let rnx = tz*edgeRy - ty*edgeRz, rny = tx*edgeRz - tz*edgeRx, rnz = ty*edgeRx - tx*edgeRy;
    let rl = Math.sqrt(rnx*rnx+rny*rny+rnz*rnz);
    if (rl > 0) { rnx/=rl; rny/=rl; rnz/=rl; }

    const edgeTx = s.tr[0]-s.tl[0], edgeTy = s.tr[1]-s.tl[1], edgeTz = s.tr[2]-s.tl[2];
    let tnx = ty*edgeTz - tz*edgeTy, tny = tz*edgeTx - tx*edgeTz, tnz = tx*edgeTy - ty*edgeTx;
    let tl = Math.sqrt(tnx*tnx+tny*tny+tnz*tnz);
    if (tl > 0) { tnx/=tl; tny/=tl; tnz/=tl; }
    if (tny < 0) { tnx=-tnx; tny=-tny; tnz=-tnz; }

    const edgeBx = s.br[0]-s.bl[0], edgeBy = s.br[1]-s.bl[1], edgeBz = s.br[2]-s.bl[2];
    let bnx = tz*edgeBy - ty*edgeBz, bny = tx*edgeBz - tz*edgeBx, bnz = ty*edgeBx - tx*edgeBy;
    let bl2 = Math.sqrt(bnx*bnx+bny*bny+bnz*bnz);
    if (bl2 > 0) { bnx/=bl2; bny/=bl2; bnz/=bl2; }
    if (bny > 0) { bnx=-bnx; bny=-bny; bnz=-bnz; }

    secNormals.push({ left: [lnx,lny,lnz], right: [rnx,rny,rnz], top: [tnx,tny,tnz], bot: [bnx,bny,bnz] });
  }

  // Emit quads with smooth per-vertex normals
  for (let i = 0; i < N; i++) {
    const s0 = sections[i], s1 = sections[i + 1];
    const n0 = secNormals[i], n1 = secNormals[i + 1];

    // Left face: s0.bl→s1.bl→s1.tl→s0.tl
    vert(s0.bl, n0.left[0],n0.left[1],n0.left[2], rL,gL,bL);
    vert(s1.bl, n1.left[0],n1.left[1],n1.left[2], rL,gL,bL);
    vert(s1.tl, n1.left[0],n1.left[1],n1.left[2], rL,gL,bL);
    vert(s0.bl, n0.left[0],n0.left[1],n0.left[2], rL,gL,bL);
    vert(s1.tl, n1.left[0],n1.left[1],n1.left[2], rL,gL,bL);
    vert(s0.tl, n0.left[0],n0.left[1],n0.left[2], rL,gL,bL);

    // Right face: s0.br→s0.tr→s1.tr→s1.br
    vert(s0.br, n0.right[0],n0.right[1],n0.right[2], rR,gR,bR);
    vert(s0.tr, n0.right[0],n0.right[1],n0.right[2], rR,gR,bR);
    vert(s1.tr, n1.right[0],n1.right[1],n1.right[2], rR,gR,bR);
    vert(s0.br, n0.right[0],n0.right[1],n0.right[2], rR,gR,bR);
    vert(s1.tr, n1.right[0],n1.right[1],n1.right[2], rR,gR,bR);
    vert(s1.br, n1.right[0],n1.right[1],n1.right[2], rR,gR,bR);

    // Top face: s0.tl→s1.tl→s1.tr→s0.tr
    vert(s0.tl, n0.top[0],n0.top[1],n0.top[2], rT,gT,bT);
    vert(s1.tl, n1.top[0],n1.top[1],n1.top[2], rT,gT,bT);
    vert(s1.tr, n1.top[0],n1.top[1],n1.top[2], rT,gT,bT);
    vert(s0.tl, n0.top[0],n0.top[1],n0.top[2], rT,gT,bT);
    vert(s1.tr, n1.top[0],n1.top[1],n1.top[2], rT,gT,bT);
    vert(s0.tr, n0.top[0],n0.top[1],n0.top[2], rT,gT,bT);

    // Bottom face: s0.bl→s0.br→s1.br→s1.bl
    vert(s0.bl, n0.bot[0],n0.bot[1],n0.bot[2], 0.15,0.15,0.17);
    vert(s0.br, n0.bot[0],n0.bot[1],n0.bot[2], 0.15,0.15,0.17);
    vert(s1.br, n1.bot[0],n1.bot[1],n1.bot[2], 0.15,0.15,0.17);
    vert(s0.bl, n0.bot[0],n0.bot[1],n0.bot[2], 0.15,0.15,0.17);
    vert(s1.br, n1.bot[0],n1.bot[1],n1.bot[2], 0.15,0.15,0.17);
    vert(s1.bl, n1.bot[0],n1.bot[1],n1.bot[2], 0.15,0.15,0.17);
  }

  // End caps (flat normals)
  const first = sections[0], last = sections[N];
  flatQuad(first.bl, first.br, first.tr, first.tl, 0.2, 0.2, 0.22);
  flatQuad(last.bl, last.tl, last.tr, last.br, 0.2, 0.2, 0.22);

  return verts;
}

// --- Ramp collision ---

/** Generate collision brushes for a ramp. One brush per segment,
 *  with planes derived from the cross-section corners (tangent-aware + yaw-rotated). */
export function buildRampCollision(r: RampEntity): Brush[] {
  const sections = sampleRampSections(r);
  const N = sections.length - 1;
  const brushes: Brush[] = [];

  for (let i = 0; i < N; i++) {
    const s0 = sections[i], s1 = sections[i + 1];
    const corners = [s0.bl, s0.br, s0.tl, s0.tr, s1.bl, s1.br, s1.tl, s1.tr];

    // Segment center for normal orientation
    let midX = 0, midY = 0, midZ = 0;
    for (const c of corners) { midX += c[0]; midY += c[1]; midZ += c[2]; }
    midX /= 8; midY /= 8; midZ /= 8;

    function makePlane(a: [number,number,number], b: [number,number,number], c: [number,number,number]): { n: [number,number,number]; d: number } {
      const n = planeNormalFromTri(a, b, c);
      const dx = a[0]-midX, dy = a[1]-midY, dz = a[2]-midZ;
      if (n[0]*dx + n[1]*dy + n[2]*dz < 0) { n[0]=-n[0]; n[1]=-n[1]; n[2]=-n[2]; }
      const d = n[0]*a[0] + n[1]*a[1] + n[2]*a[2];
      return { n, d };
    }

    function toFP(p: { n: [number,number,number]; d: number }): Plane {
      return { nx: FP.from(p.n[0]), ny: FP.from(p.n[1]), nz: FP.from(p.n[2]), d: FP.from(p.d) };
    }

    // SURFACE planes first — actual ramp geometry.
    // Only hits through these are accepted (Q3 facet collision).
    const bot   = makePlane(s0.bl, s1.bl, s1.br);
    const top   = makePlane(s0.tl, s1.tr, s1.tl);
    const left  = makePlane(s0.bl, s0.tl, s1.tl);
    const right = makePlane(s0.br, s1.tr, s0.tr);
    const front = makePlane(s0.bl, s0.br, s0.tl);
    const back  = makePlane(s1.bl, s1.tl, s1.br);

    const planes: Plane[] = [toFP(bot), toFP(top), toFP(left), toFP(right)];
    planes.push(toFP(front), toFP(back));

    // First and last segments: regular brush (all planes are surface planes).
    // No adjacent segment to transition to, so catching can't happen, and
    // the end caps need full collision to be solid walls.
    // Internal segments: facet mode (surface/border discrimination).
    // Only the 4 geometry planes are surface; caps + axials are borders.
    const isEndSegment = i === 0 || i === N - 1;
    const surfacePlaneCount = isEndSegment ? undefined : 4;

    // Axial containment (AABB)
    let bMinX = Infinity, bMinY = Infinity, bMinZ = Infinity;
    let bMaxX = -Infinity, bMaxY = -Infinity, bMaxZ = -Infinity;
    for (const c of corners) {
      if (c[0] < bMinX) bMinX = c[0]; if (c[0] > bMaxX) bMaxX = c[0];
      if (c[1] < bMinY) bMinY = c[1]; if (c[1] > bMaxY) bMaxY = c[1];
      if (c[2] < bMinZ) bMinZ = c[2]; if (c[2] > bMaxZ) bMaxZ = c[2];
    }
    planes.push(
      { nx: FP.from(1), ny: FP.ZERO, nz: FP.ZERO, d: FP.from(bMaxX) },
      { nx: FP.from(-1) as Fixed, ny: FP.ZERO, nz: FP.ZERO, d: FP.from(-bMinX) },
      { nx: FP.ZERO, ny: FP.from(1), nz: FP.ZERO, d: FP.from(bMaxY) },
      { nx: FP.ZERO, ny: FP.from(-1) as Fixed, nz: FP.ZERO, d: FP.from(-bMinY) },
      { nx: FP.ZERO, ny: FP.ZERO, nz: FP.from(1), d: FP.from(bMaxZ) },
      { nx: FP.ZERO, ny: FP.ZERO, nz: FP.from(-1) as Fixed, d: FP.from(-bMinZ) },
    );

    const bounds = {
      minX: FP.from(bMinX), minY: FP.from(bMinY), minZ: FP.from(bMinZ),
      maxX: FP.from(bMaxX), maxY: FP.from(bMaxY), maxZ: FP.from(bMaxZ),
    };

    brushes.push({ planes, bounds, surfacePlaneCount });
  }
  return brushes;
}

/** Compute a unit normal from three coplanar points (triangle) */
function planeNormalFromTri(a: [number,number,number], b: [number,number,number], c: [number,number,number]): [number,number,number] {
  const e1x = b[0]-a[0], e1y = b[1]-a[1], e1z = b[2]-a[2];
  const e2x = c[0]-a[0], e2y = c[1]-a[1], e2z = c[2]-a[2];
  let nx = e1y*e2z - e1z*e2y, ny = e1z*e2x - e1x*e2z, nz = e1x*e2y - e1y*e2x;
  const len = Math.sqrt(nx*nx + ny*ny + nz*nz);
  if (len > 0) { nx /= len; ny /= len; nz /= len; }
  return [nx, ny, nz];
}

// --- Builders: SceneConfig → runtime ---

/** Build collision brushes from all entities */
const MATERIAL_MAP: Record<string, number> = { interior: 0, exterior: 1 };

export function buildBrushes(scene: SceneConfig): Brush[] {
  const brushes: Brush[] = [];
  for (const e of scene.entities) {
    if (e.type === "brush") {
      const b = aabbToBrush(aabb(e.x, e.y, e.z, e.w, e.h, e.d));
      b.material = MATERIAL_MAP[e.material ?? "interior"] ?? 0;
      brushes.push(b);
    }
    if (e.type === "ramp") {
      const mat = MATERIAL_MAP[e.material ?? "interior"] ?? 0;
      const rampBrushes = buildRampCollision(e);
      for (const b of rampBrushes) b.material = mat;
      brushes.push(...rampBrushes);
    }
  }
  return brushes;
}

/** Build ECS systems */
export function buildSceneSystems(
  scene: SceneConfig, stores: ComponentStores, world: World,
  controllerConfig?: import("./movement.js").PlayerControllerConfig,
): System[] {
  const collisionBrushes = buildBrushes(scene);
  const grounded = new BitSet();
  const gravity = FP.from(scene.gravity ?? 20);

  const systems: System[] = [
    createPlayerController(stores.position, stores.velocity, stores.playerId, controllerConfig, grounded),
    createPhysicsSystem(stores.position, stores.velocity, {
      gravity, brushes: collisionBrushes,
      halfWidth: FP.from(0.4), halfHeight: FP.from(0.9), halfDepth: FP.from(0.4),
    }, grounded),
    createBuoyancySystem(stores.position, stores.velocity, world),
  ];

  const BOUND_MIN = FP.from(-500), BOUND_MAX = FP.from(500);
  systems.push(() => {
    const { ids, count } = query(stores.position.mask);
    for (let i = 0; i < count; i++) {
      const id = ids[i];
      stores.position.x[id] = FP.clamp(stores.position.x[id], BOUND_MIN, BOUND_MAX);
      stores.position.z[id] = FP.clamp(stores.position.z[id], BOUND_MIN, BOUND_MAX);
    }
  });

  // Trigger system — checks AABB overlap between players and trigger zones
  const triggers = entitiesOfType(scene.entities, "trigger");
  if (triggers.length > 0) {
    // Touch state: tracks which entities are inside which triggers
    // Packed as: touchState[entityId * maxTriggers + triggerIdx]
    const maxTriggers = triggers.length;
    const touchState = new Uint8Array(4096 * maxTriggers); // 0 = outside, 1 = inside

    // Per-player timer: startTick (0 = not started)
    const timerStart = new Int32Array(64);
    const timerRunning = new Uint8Array(64);
    // Per-player checkpoint position
    const checkpointX = new Int32Array(64);
    const checkpointY = new Int32Array(64);
    const checkpointZ = new Int32Array(64);

    // Convert trigger bounds to fixed-point once
    const tMinX = triggers.map(t => FP.from(t.x - t.w / 2));
    const tMaxX = triggers.map(t => FP.from(t.x + t.w / 2));
    const tMinY = triggers.map(t => FP.from(t.y - t.h / 2));
    const tMaxY = triggers.map(t => FP.from(t.y + t.h / 2));
    const tMinZ = triggers.map(t => FP.from(t.z - t.d / 2));
    const tMaxZ = triggers.map(t => FP.from(t.z + t.d / 2));

    systems.push(() => {
      const { ids, count } = query(stores.position.mask, stores.playerId.mask);
      for (let p = 0; p < count; p++) {
        const id = ids[p];
        const px = stores.position.x[id];
        const py = stores.position.y[id];
        const pz = stores.position.z[id];
        const pid = stores.playerId.data[id];

        for (let t = 0; t < maxTriggers; t++) {
          const inside = px >= tMinX[t] && px <= tMaxX[t]
                      && py >= tMinY[t] && py <= tMaxY[t]
                      && pz >= tMinZ[t] && pz <= tMaxZ[t];
          const key = id * maxTriggers + t;
          const wasInside = touchState[key] === 1;

          if (inside && !wasInside) {
            // ENTER
            touchState[key] = 1;
            const trigger = triggers[t];
            switch (trigger.action) {
              case "start":
                timerStart[pid] = world.tick;
                timerRunning[pid] = 1;
                break;
              case "end":
                if (timerRunning[pid]) {
                  const ticks = world.tick - timerStart[pid];
                  simEvents.push(SimEventType.TIMER_STOP, pid, ticks);
                  timerRunning[pid] = 0;
                }
                break;
              case "checkpoint":
                checkpointX[pid] = px;
                checkpointY[pid] = py;
                checkpointZ[pid] = pz;
                simEvents.push(SimEventType.CHECKPOINT, pid, trigger.param ?? 0);
                break;
              case "teleport":
                if (trigger.target) {
                  stores.position.set(id,
                    FP.from(trigger.target[0]),
                    FP.from(trigger.target[1]),
                    FP.from(trigger.target[2]),
                  );
                  stores.velocity.set(id, 0, 0, 0);
                } else if (checkpointX[pid] !== 0 || checkpointY[pid] !== 0 || checkpointZ[pid] !== 0) {
                  // Teleport to last checkpoint
                  stores.position.x[id] = checkpointX[pid];
                  stores.position.y[id] = checkpointY[pid];
                  stores.position.z[id] = checkpointZ[pid];
                  stores.velocity.set(id, 0, 0, 0);
                }
                break;
              case "push":
                stores.velocity.y[id] = FP.add(stores.velocity.y[id], FP.from(trigger.param ?? 10));
                break;
            }
          }

          // Push applies every tick while inside
          if (inside && triggers[t].action === "push") {
            const tgt = triggers[t].target; // [vx, vy, vz] velocity to apply
            if (tgt) {
              stores.velocity.x[id] = FP.add(stores.velocity.x[id], FP.from(tgt[0]));
              stores.velocity.y[id] = FP.add(stores.velocity.y[id], FP.from(tgt[1]));
              stores.velocity.z[id] = FP.add(stores.velocity.z[id], FP.from(tgt[2]));
            } else {
              stores.velocity.y[id] = FP.add(stores.velocity.y[id], FP.from(triggers[t].param ?? 10));
            }
            if (grounded) grounded.clear(id);
          }

          if (!inside && wasInside) {
            touchState[key] = 0;
          }
        }

        // Send timer update to main thread every tick (for HUD)
        if (timerRunning[pid]) {
          simEvents.push(SimEventType.TIMER_UPDATE, pid, world.tick - timerStart[pid]);
        }
      }
    });
  }

  return systems;
}

/** Spawn player entities */
export function spawnPlayers(
  scene: SceneConfig, world: World, playerCount: number, stores: ComponentStores,
): void {
  const colors = ["#e74c3c", "#3498db", "#2ecc71", "#f39c12"];
  const spawns = entitiesOfType(scene.entities, "spawn");
  if (spawns.length === 0) return;

  for (let i = 0; i < playerCount; i++) {
    const spawn = spawns[i % spawns.length];
    const id = world.createEntity();
    stores.position.set(id, FP.from(spawn.pos[0]), FP.from(spawn.pos[1]), FP.from(spawn.pos[2]));
    stores.velocity.set(id, 0, 0, 0);
    stores.playerId.set(id, i);
    stores.renderSize.set(id, spawn.size ?? 1);
    stores.renderColor.set(id, colors[i % colors.length]);
  }
}

/** Spawn a single player entity. Returns the entity ID. */
export function spawnPlayer(
  scene: SceneConfig, world: World, peerId: number, stores: ComponentStores,
): number {
  const colors = ["#e74c3c", "#3498db", "#2ecc71", "#f39c12"];
  const spawns = entitiesOfType(scene.entities, "spawn");
  if (spawns.length === 0) return -1;
  const spawn = spawns[peerId % spawns.length];
  const id = world.createEntity();
  stores.position.set(id, FP.from(spawn.pos[0]), FP.from(spawn.pos[1]), FP.from(spawn.pos[2]));
  stores.velocity.set(id, 0, 0, 0);
  stores.playerId.set(id, peerId);
  stores.renderSize.set(id, spawn.size ?? 1);
  stores.renderColor.set(id, colors[peerId % colors.length]);
  return id;
}

/** Build render config from scene data */
export function buildSceneRender(scene: SceneConfig): SceneRenderConfig {
  const brushEntities = entitiesOfType(scene.entities, "brush");
  const rampEntities = entitiesOfType(scene.entities, "ramp");
  const lightEntities = entitiesOfType(scene.entities, "light");

  // Compute voxelBounds
  let voxelBounds = scene.voxelBounds;
  if (!voxelBounds) {
    let minX = -10, minY = -2, minZ = -10, maxX = 10, maxY = 10, maxZ = 10;
    for (const b of brushEntities) {
      minX = Math.min(minX, b.x - b.w/2); minY = Math.min(minY, b.y - b.h/2);
      minZ = Math.min(minZ, b.z - b.d/2); maxX = Math.max(maxX, b.x + b.w/2);
      maxY = Math.max(maxY, b.y + b.h/2); maxZ = Math.max(maxZ, b.z + b.d/2);
    }
    for (const r of rampEntities) {
      // For rotated ramps, use the bounding circle of the ramp's footprint
      const halfExtent = Math.max(r.baseW, r.length) / 2;
      minX = Math.min(minX, r.x - halfExtent); minZ = Math.min(minZ, r.z - halfExtent);
      maxX = Math.max(maxX, r.x + halfExtent); maxY = Math.max(maxY, (r.y ?? 0) + r.height);
      maxZ = Math.max(maxZ, r.z + halfExtent);
    }
    // Generous padding so shadows/GI can extend well beyond geometry.
    // Vertical padding is extra large for overhead shadows and tall structures.
    const extentX = maxX - minX, extentY = maxY - minY, extentZ = maxZ - minZ;
    const padH = Math.max(10, Math.max(extentX, extentZ) * 0.5);
    const padV = Math.max(20, extentY * 2);
    voxelBounds = { min: [minX-padH, minY-4, minZ-padH], max: [maxX+padH, maxY+padV, maxZ+padH] };
  }

  // Voxel boxes (noVoxel brushes are visible but don't cast shadows or participate in GI)
  const voxelBoxes: SceneRenderConfig["voxelBoxes"] = brushEntities
    .filter(b => !b.noVoxel)
    .map(b => {
      const [r, g, bb] = hexToRgb(b.color);
      return { x: b.x, y: b.y, z: b.z, w: b.w, h: b.h, d: b.d, r, g, b: bb, emissive: b.emissive };
    });
  for (const r of rampEntities) {
    voxelBoxes.push({
      x: r.x, y: (r.y ?? 0) + r.height/2, z: r.z, w: r.baseW, h: r.height, d: r.length,
      r: 0.25, g: 0.25, b: 0.28,
    });
  }

  // Mesh vertices from ramps
  let meshVertices: Float32Array | undefined;
  if (rampEntities.length > 0) {
    const allVerts: number[] = [];
    for (const r of rampEntities) allVerts.push(...surfRampMesh(r));
    meshVertices = new Float32Array(allVerts);
  }

  // Lights
  const lights: PointLightDef[] = lightEntities.map(l => ({
    pos: l.pos, radius: l.radius, color: l.color, intensity: l.intensity,
  }));

  return {
    ocean: scene.ocean ?? true,
    voxelBounds,
    lights,
    fog: scene.fog ?? { color: [0.071, 0.110, 0.169], density: 0.002 },
    giStrength: scene.giStrength ?? 0.07,
    reflStrength: scene.reflStrength ?? 0.55,
    sunDir: scene.sunDir ?? [-0.16, 0.48, -0.32],
    voxelBoxes,
    sceneBoxes: brushEntities,
    meshVertices,
  };
}
